import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
	Autocomplete,
	Box,
	Checkbox,
	createFilterOptions,
	IconButton,
	TextField,
} from "@mui/material";
import {
	type FocusEvent,
	type KeyboardEvent,
	type MouseEvent,
	useRef,
} from "react";
import { omit } from "lodash-es";
import { capsSx } from "@/pages/sawlines/components/primitives";
import type {
	MillBundle,
	MillHeat,
	MillSection,
} from "../repositories/tagStationRepository";
import {
	carryInches,
	changed,
	type Draft,
	draftOf,
	FIELDS,
	type Field,
	invalid,
	sent,
	sectionLabel,
	shown,
	type Values,
	valuesOf,
	type Work,
} from "../tagStation";
import { Card, Count, labelSx } from "./Card";
import { heatOption } from "./NewBundleModal";

const TONE = {
	ok: { border: "#4CAF50", bg: "#f3faf4", text: "#1b5e20", mark: "#2e7d32" },
	fixed: { border: "#ed6c02", bg: "#fff8ef", text: "#7a3e00", mark: "#b26a00" },
} as const;

const squareSx = { width: 30, height: 30, borderRadius: 1, border: 1 } as const;

type TypedField = "pcs" | "len";
type ListField = Exclude<Field, TypedField>;
const isList = (f: Field): f is ListField => f !== "pcs" && f !== "len";
const filterHeats = createFilterOptions<string>();

// input with no chrome: the row's box is the field
const bareSx = {
	flex: 1,
	minWidth: 0,
	height: "100%",
	border: 0,
	outline: 0,
	bgcolor: "transparent",
	font: "inherit",
	color: "inherit",
	p: 0,
} as const;

// own row, while editing: a blur would confirm before the click lands
const keepFocus = (e: MouseEvent) => e.preventDefault();

export const CheckCard = ({
	bundle,
	work,
	sections,
	grades,
	heats,
	comments,
	onWork,
	onInvalid,
}: {
	bundle: MillBundle;
	work: Work;
	sections: MillSection[];
	grades: string[];
	heats: MillHeat[];
	comments: string[];
	onWork: (work: Work) => void;
	onInvalid: (message: string) => void;
}) => {
	const card = useRef<HTMLDivElement>(null);
	// Enter / Esc belong to the option list while it is open
	const listOpen = useRef(false);
	const printed = bundle.State === "Printed";
	const arrived = sent(bundle);
	const values = valuesOf(work.draft);
	const diff = changed(arrived, values);
	// first one still to confirm; no bar while it is being edited
	const pending = printed
		? undefined
		: FIELDS.find(([f]) => !work.confirmed[f])?.[0];
	const next = pending && !work.editing[pending] ? pending : undefined;
	const done = FIELDS.filter(([f]) => work.confirmed[f]).length;

	const label = (v: Values, f: Field) =>
		f === "section" ? sectionLabel(v.section, sections) : shown(v, f);
	const set = (patch: Partial<Draft>) =>
		onWork({ ...work, draft: { ...work.draft, ...patch } });

	// Finishing an edit confirms it: nobody leaves a value changed but unchecked.
	const confirm = (f: Field, patch: Partial<Draft> = {}, moveOn = true) => {
		let draft = { ...work.draft, ...patch };
		if (f === "len") draft = { ...draft, ...carryInches(draft.ft, draft.inch) };
		const bad = invalid(valuesOf(draft), f);
		if (bad) return onInvalid(bad);
		onWork({
			draft,
			confirmed: { ...work.confirmed, [f]: true },
			editing: omit(work.editing, f),
		});
		// keyboard flow: on to the next value, then to Print
		if (moveOn)
			setTimeout(() =>
				(
					card.current?.querySelector<HTMLElement>(
						"[data-next] input[type=checkbox]",
					) ?? document.querySelector<HTMLElement>("[data-print-tag]")
				)?.focus(),
			);
	};
	const toggleEdit = (f: Field) => {
		if (!work.editing[f])
			return onWork({
				...work,
				confirmed: omit(work.confirmed, f),
				editing: { ...work.editing, [f]: true },
			});
		// cancel: back to what arrived
		const d = draftOf(bundle);
		onWork({
			...work,
			draft: {
				...work.draft,
				...(f === "len" ? { ft: d.ft, inch: d.inch } : { [f]: d[f] }),
			},
			editing: omit(work.editing, f),
		});
	};
	const toggleOk = (f: Field) =>
		work.confirmed[f]
			? onWork({ ...work, confirmed: omit(work.confirmed, f) })
			: confirm(f);
	const keys = (f: Field) => (e: KeyboardEvent) => {
		if (listOpen.current) return;
		if (e.key === "Enter") {
			e.preventDefault();
			confirm(f);
		} else if (e.key === "Escape") {
			e.preventDefault();
			toggleEdit(f);
		}
	};

	// Grade / Section: only values of the mill catalog. Heat: the mill's list, or typed off the bars.
	const picker = (f: ListField, name: string) => {
		const current = work.draft[f];
		const free = f === "heat";
		const options = free
			? heats.map((h) => h.HeatNo)
			: f === "grade"
				? grades
				: sections.map((s) => s.SectionName);
		return (
			<Autocomplete
				fullWidth
				openOnFocus
				disableClearable
				freeSolo={free}
				// what arrived stays pickable even if the catalog dropped it
				options={
					free || !current || options.includes(current)
						? options
						: [current, ...options]
				}
				getOptionLabel={(option) => label({ ...values, [f]: option }, f)}
				// heat: the text is the value, so any pick or Enter is a change
				value={free ? "" : current || undefined}
				inputValue={free ? current : undefined}
				onInputChange={
					free
						? (_, heat, reason) => reason === "input" && set({ heat })
						: undefined
				}
				// whole list until the operator types
				filterOptions={
					free
						? (all, state) =>
								state.inputValue === arrived.heat
									? all
									: filterHeats(all, state)
						: undefined
				}
				renderOption={free ? heatOption(heats) : undefined}
				onChange={(_, value) => confirm(f, { [f]: value })}
				onOpen={() => {
					listOpen.current = true;
				}}
				onClose={() => {
					listOpen.current = false;
				}}
				onKeyDown={keys(f)}
				sx={{ flex: 1, minWidth: 0, height: "100%" }}
				renderInput={(params) => (
					// the list anchors here: struck value included, so it spans the field
					<Box
						ref={params.InputProps.ref}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.25,
							height: "100%",
						}}
					>
						<s>{shown(arrived, f) || "—"}</s>
						<Box
							component="input"
							{...params.inputProps}
							autoFocus
							// typing replaces the value instead of adding to it
							onFocus={(e: FocusEvent<HTMLInputElement>) => {
								params.inputProps.onFocus?.(e);
								e.target.select();
							}}
							aria-label={name}
							placeholder={free ? "Read it off the bars" : undefined}
							maxLength={free ? 20 : undefined}
							sx={bareSx}
						/>
						<ArrowDropDownIcon sx={{ color: "action.active" }} />
					</Box>
				)}
			/>
		);
	};

	const control = (f: TypedField) => {
		const d = work.draft;
		const onKeyDown = keys(f);
		if (f === "len")
			return (
				<>
					<Box
						component="input"
						type="number"
						min={0}
						autoFocus
						value={d.ft}
						onChange={(e) => set({ ft: e.target.value })}
						onKeyDown={onKeyDown}
						aria-label="Length feet"
						sx={{ ...bareSx, flex: "0 0 54px" }}
					/>
					<Unit>ft</Unit>
					<Box
						component="input"
						type="number"
						min={0}
						value={d.inch}
						onChange={(e) => set({ inch: e.target.value })}
						onKeyDown={onKeyDown}
						aria-label="Length inches"
						sx={{ ...bareSx, flex: "0 0 54px" }}
					/>
					<Unit>in</Unit>
				</>
			);
		return (
			<Box
				component="input"
				type="number"
				autoFocus
				value={d.pcs}
				onChange={(e) => set({ pcs: e.target.value })}
				onKeyDown={onKeyDown}
				aria-label="Pieces"
				sx={bareSx}
			/>
		);
	};

	return (
		<Card
			title="Check against the bundle"
			sub={printed ? "as printed" : undefined}
			action={
				!printed && (
					<Count gray={done < FIELDS.length}>
						{done} of {FIELDS.length}
					</Count>
				)
			}
		>
			<Box
				ref={card}
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					p: 1,
					pb: 1.5,
				}}
			>
				{FIELDS.map(([f, name]) => {
					const editing = !printed && !!work.editing[f];
					const confirmed = printed || !!work.confirmed[f];
					const corrected = diff.includes(f);
					const tone =
						!confirmed || editing ? null : TONE[corrected ? "fixed" : "ok"];
					return (
						<Box
							key={f}
							data-next={next === f ? "" : undefined}
							sx={{
								display: "grid",
								gridTemplateColumns: "76px minmax(0, 1fr) auto",
								alignItems: "center",
								columnGap: 1.5,
								py: 0.5,
								"@media (max-height:960px)": { py: 0.4 },
								pl: 1.25,
								pr: 1,
								borderBottom: "1px solid #f1f1f1",
								borderLeft: "3px solid transparent",
								...(next === f && {
									borderLeftColor: "primary.main",
									bgcolor: "#f6fbf6",
								}),
							}}
						>
							<Box sx={labelSx}>{name}</Box>
							<Box
								role={editing ? undefined : "textbox"}
								aria-readonly={editing ? undefined : true}
								aria-label={editing ? undefined : name}
								onBlur={(e) =>
									editing &&
									!e.currentTarget.contains(e.relatedTarget) &&
									confirm(f, {}, false)
								}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									height: 40,
									px: editing ? "11px" : 1.5,
									border: editing ? 2 : 1,
									borderColor: editing
										? "primary.main"
										: (tone?.border ?? "rgba(0,0,0,.23)"),
									borderRadius: 1,
									bgcolor: tone?.bg ?? "#fff",
									color: tone?.text ?? "text.primary",
									fontSize: "1.1rem",
									fontWeight: 600,
									fontVariantNumeric: "tabular-nums",
									minWidth: 0,
									overflow: "hidden",
									whiteSpace: "nowrap",
									// locked: looks like a field, behaves like a label
									...(!editing && { cursor: "default", userSelect: "none" }),
									"& s": {
										color: tone === TONE.fixed ? "#a06a00" : "text.secondary",
										fontWeight: 400,
										fontSize: "0.95rem",
										flexShrink: 0,
										maxWidth: "45%",
										overflow: "hidden",
										textOverflow: "ellipsis",
									},
								}}
							>
								{/* arrived value stays struck while editing and once corrected */}
								{(editing ? !isList(f) : corrected) && (
									<s>{shown(arrived, f) || "—"}</s>
								)}
								{!editing ? (
									<Box
										component="span"
										sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
									>
										{label(values, f)}
									</Box>
								) : isList(f) ? (
									picker(f, name)
								) : (
									control(f)
								)}
								{tone && (
									<CheckCircleIcon
										sx={{ ml: "auto", fontSize: 22, color: tone.mark }}
									/>
								)}
							</Box>
							{!printed && (
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									{/* green only while there is something left to do */}
									<IconButton
										title={editing ? "Cancel the change" : "Change it"}
										aria-label={`${editing ? "Cancel change of" : "Change"} ${name}`}
										color={
											editing ? "error" : confirmed ? "default" : "primary"
										}
										onMouseDown={editing ? keepFocus : undefined}
										onClick={() => toggleEdit(f)}
										sx={{ ...squareSx, borderColor: "currentColor" }}
									>
										{editing ? (
											<CloseIcon fontSize="small" />
										) : (
											<EditIcon fontSize="small" />
										)}
									</IconButton>
									<Checkbox
										title={
											work.confirmed[f]
												? "Confirmed. Press to undo"
												: "Confirm it matches the bundle"
										}
										checked={!!work.confirmed[f]}
										onMouseDown={editing ? keepFocus : undefined}
										onChange={() => toggleOk(f)}
										// a checkbox only takes Space; Enter keeps the keyboard flow
										onKeyDown={(e) => e.key === "Enter" && toggleOk(f)}
										inputProps={{ "aria-label": `Confirm ${name}` }}
										// 40px icon draws a 30px box, the pencil's size
										sx={{
											p: 0,
											m: "-5px",
											"& .MuiSvgIcon-root": { fontSize: 40 },
										}}
									/>
								</Box>
							)}
						</Box>
					);
				})}
				<Box sx={{ mt: "auto", pt: 1.5, px: 1.25 }}>
					{printed ? (
						<>
							<Box sx={capsSx}>Comment</Box>
							<Box sx={{ mt: 0.25 }}>{bundle.Comment || "—"}</Box>
						</>
					) : (
						<Autocomplete
							freeSolo
							size="small"
							options={comments}
							inputValue={work.draft.comment}
							onInputChange={(_, comment) => set({ comment })}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Comment"
									margin="none"
									placeholder="Type it or pick one used before"
									inputProps={{ ...params.inputProps, maxLength: 200 }}
								/>
							)}
						/>
					)}
				</Box>
			</Box>
		</Card>
	);
};

const Unit = ({ children }: { children: string }) => (
	<Box
		component="span"
		sx={{
			fontSize: "0.8rem",
			fontWeight: 400,
			color: "text.secondary",
			ml: -0.5,
		}}
	>
		{children}
	</Box>
);
